"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const parameters = [{
  id: "param-1",
  title: "Niwelacja terenu",
  description: "Kształtowanie docelowych spadków działki z dokładnością geodezyjną."
}, {
  id: "param-2",
  title: "Podłoże pod fundamenty",
  description: "Korytowanie, wymiana gruntu i mechaniczne zagęszczanie warstwowe."
}, {
  id: "param-3",
  title: "Utwardzanie dojazdów",
  description: "Stabilizacja dróg tymczasowych oraz podjazdów frakcjonowanym kruszywem."
}];
export default function Earthworks() {
  const shouldReduceMotion = useReducedMotion();
  const motionInitial = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="prace-ziemne" data-nav="dark" className="dark relative min-h-[720px] w-full overflow-hidden bg-background text-foreground py-24 md:py-32">
      {/* Full-bleed documentary background photo */}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/earthworks_heavy_machinery.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/earthworks_heavy_machinery.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/earthworks_heavy_machinery.png-wvc-sizes" alt="Koparka gąsienicowa podczas profilowania i zagęszczania gruntu pod budowę domu" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
        {/* Cool-tone architectural gradient scrim */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/70" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        {/* Architectural territorial badge */}
        <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
        once: true
      }} transition={motionTransition} className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-border/70 bg-card/60 px-4 py-1.5 backdrop-blur-md">
          <span className="size-2 rounded-full bg-accent" />
          <span className="font-mono text-xs font-semibold tracking-wider uppercase text-foreground">
            Bielsko-Biała + zasięg do 25 km
          </span>
        </motion.div>

        {/* Central command glass module */}
        <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
        once: true
      }} transition={{
        ...motionTransition,
        delay: shouldReduceMotion ? 0 : 0.08
      }} className="relative max-w-3xl rounded-xl border border-border/80 bg-card/75 p-8 md:p-12 shadow-2xl backdrop-blur-xl">
          {/* Architectural datum notch motif */}
          <div className="absolute top-0 left-8 h-[2px] w-12 bg-accent" />

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            prace ziemne Bielsko-Biała
          </h2>

          <p className="mt-4 text-lg font-light leading-relaxed text-muted-foreground md:text-xl">
            Przygotowujemy działki pod budowę domów jednorodzinnych i obiektów kubaturowych.
            Realizujemy kompleksowe roboty ziemne, wymianę podłoża oraz stabilizację mechaniczną.
          </p>

          <p className="mt-2 text-sm leading-relaxed text-muted-foreground/90 md:text-base">
            Własny park maszynowy umożliwia sprawne korytowanie, precyzyjne niwelacje terenu
            oraz formowanie skarp zgodnie z założeniami projektu budowlanego.
          </p>

          {/* Action CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="#kontakt">
                Zamów wycenę robót
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted/50">
              <a href="tel:796280315" className="inline-flex items-center gap-2">
                <Phone className="size-4 text-accent" />
                <span className="font-mono">+48 796 280 315</span>
              </a>
            </Button>
          </div>
        </motion.div>

        {/* 3-point inline parameter strip */}
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {parameters.map((param, i) => <motion.div key={param.id} data-index={i} initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true
        }} transition={{
          ...motionTransition,
          delay: shouldReduceMotion ? 0 : 0.12 + i * 0.06
        }} className="flex flex-col justify-between rounded-lg border border-border/70 bg-card/65 p-6 backdrop-blur-md transition-[border-color,box-shadow] duration-200 hover:border-accent/80 hover:shadow-lg">
              <div>
                <div className="flex items-center gap-3">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <h3 className="font-default text-lg font-semibold text-foreground">
                    {param.title}
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {param.description}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-border/40 pt-4">
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Standard BUDSYS
                </span>
                <span className="font-mono text-xs font-medium text-accent">
                  0{i + 1}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}